import { useEffect, useRef, useState } from 'react';

// Pull-to-refresh, only active when the app is running in PWA standalone
// mode (i.e. installed to the home screen). In regular Safari, the native
// pull-to-refresh already works, so we don't fight it. Past a threshold,
// we hard-reload — unregistering the service worker and clearing caches —
// so the page picks up any newly deployed JS chunks.

const THRESHOLD = 80;
const MAX_VISUAL_PULL = 120;
const RESISTANCE = 0.45;

async function hardReload() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // best-effort
  }
  window.location.reload();
}

function isInsideScrolled(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el) {
    const cs = window.getComputedStyle(el);
    if (['auto', 'scroll'].includes(cs.overflowY) && el.scrollTop > 0) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

export function PullToRefresh() {
  const [pullDistance, setPullDistance] = useState(0);
  const [reloading, setReloading] = useState(false);
  const startYRef = useRef<number | null>(null);
  const distanceRef = useRef(0);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      // iOS-specific: navigator.standalone is true when launched from home screen
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (!standalone) return;

    function setDistance(d: number) {
      distanceRef.current = d;
      setPullDistance(d);
    }

    function onTouchStart(e: TouchEvent) {
      if (reloadingRef.current) return;
      if (e.touches.length !== 1) return;
      if (isInsideScrolled(e.target)) return;
      startYRef.current = e.touches[0].clientY;
      setDistance(0);
    }

    function onTouchMove(e: TouchEvent) {
      if (reloadingRef.current) return;
      if (startYRef.current === null) return;
      const raw = e.touches[0].clientY - startYRef.current;
      if (raw <= 0) {
        // Scrolling up — abort tracking
        startYRef.current = null;
        setDistance(0);
        return;
      }
      // Re-check: a scrollable ancestor may have started scrolling mid-pull
      if (isInsideScrolled(e.target)) {
        startYRef.current = null;
        setDistance(0);
        return;
      }
      // Suppress the default iOS rubber-band so our indicator stays anchored
      if (e.cancelable) e.preventDefault();
      const damped = Math.min(MAX_VISUAL_PULL, raw * RESISTANCE);
      setDistance(damped);
    }

    function onTouchEnd() {
      if (reloadingRef.current) return;
      if (startYRef.current === null) return;
      const distance = distanceRef.current;
      startYRef.current = null;
      if (distance >= THRESHOLD) {
        reloadingRef.current = true;
        setReloading(true);
        hardReload();
      } else {
        setDistance(0);
      }
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  if (pullDistance <= 0 && !reloading) return null;

  const ready = pullDistance >= THRESHOLD;
  const visualHeight = reloading ? MAX_VISUAL_PULL : pullDistance;
  const arrowRotation = reloading ? 360 : ready ? 180 : 0;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: visualHeight,
        zIndex: 9999,
        background: 'var(--paper)',
        borderBottom: '1.5px solid var(--ink)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        pointerEvents: 'none',
        transition: reloading ? 'height 0.2s' : 'none',
        boxShadow: '0 2px 0 var(--ink)',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          fontSize: 22,
          color: 'var(--brick)',
          transition: reloading ? 'none' : 'transform 0.2s',
          transform: `rotate(${arrowRotation}deg)`,
          animation: reloading ? 'ptr-spin 0.9s linear infinite' : 'none',
        }}
      >
        ↻
      </span>
      <span
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 11,
          letterSpacing: '0.18em',
          color: 'var(--ink)',
          textTransform: 'uppercase',
        }}
      >
        {reloading ? 'Reloading…' : ready ? 'Release to refresh' : 'Pull to refresh'}
      </span>
      <style>
        {`@keyframes ptr-spin { to { transform: rotate(360deg); } }`}
      </style>
    </div>
  );
}
